import React, { useCallback, useMemo, useEffect } from 'react';
import styles from './TrimmingList.module.scss';
import { Icon } from '../../../../components/Icon';
import { CustomButton } from '../../../../components/Button/Button';
import { useTrimContext } from '../../../../context/VideoTrimContex';
import { useTranslation } from 'react-i18next';
import CloseButton from '../../../../components/CloseButton/CloseButton';
import cx from 'classnames';
import SidebarButton from '../SidebarButton/SidebarButton';
import { useUserContext } from '../../../../context/UserContext';

const MIN_SPLIT_DURATION = 1000;

export const TrimmingList = ({
  activeScene,
  trimVideo,
  splitVideo,
  confirmOnClose = true,
  onClose,
  saveButtonLabel = '',
  switchingTrimming = false,
  enableSplit = true,
  miniSidebar,
  closeFeatures,
  videoId,
}) => {
  const {
    trimmingVideoDuration,
    toggleTrimmingRevert,
    trimmingActionsBuffer,
    currentActionIndex,
    undoLastAction,
    redoLastAction,
    clearAfterTrimmingDone,
    currentProgress,
    startTrimStamp,
    endTrimStamp,
    controlInUse,
  } = useTrimContext();

  const { t } = useTranslation();

  const { logFirebaseEvent } = useUserContext();

  const currentProgressSeconds = useMemo(() => {
    return currentProgress ? Math.round(currentProgress * trimmingVideoDuration * 1000) : startTrimStamp;
  }, [currentProgress, trimmingVideoDuration]);

  const handleRevert = () => {
    const confirmRevert = confirm(t('my_video.tools.trim.revert.confirm_message'));
    if (confirmRevert) {
      revertTrimming();
    }
  };

  const revertTrimming = () => {
    toggleTrimmingRevert(true);
  };

  const cancelTrimming = () => {
    clearAfterTrimmingDone();
  };

  const handleTrimming = async () => {
    trimVideo();
    clearAfterTrimmingDone();
  };

  const handleCancel = () => {
    if (confirmOnClose) {
      if (trimmingActionsBuffer.length > 1) {
        const confirmCancel = confirm(t('my_video.tools.trim.cancel.confirm_message'));
        if (confirmCancel) {
          cancelTrimming();
        }
      } else {
        cancelTrimming();
      }
    } else {
      onClose();
      cancelTrimming();
    }
  };

  useEffect(() => {
    if (miniSidebar || closeFeatures) {
      handleCancel();
    }
  }, [miniSidebar, closeFeatures]);

  const handleSplitVideo = useCallback(() => {
    logFirebaseEvent('co_split_cl', { story_id: videoId, scene_id: activeScene.id });
    splitVideo(
      currentProgressSeconds * (activeScene?.playbackRate || 1),
      startTrimStamp * (activeScene?.playbackRate || 1),
      endTrimStamp * (activeScene?.playbackRate || 1)
    );
  }, [splitVideo, currentProgressSeconds]);

  return (
    <div className={styles.wrapper}>
      <div>
        <div className={styles.trimmingHeader}>
          <h2>{t('my_video.tools.trim.title')}</h2>
          <CloseButton onClose={handleCancel} />
        </div>
        <div className={styles.listContainer}>
          <SidebarButton
            label={t('my_video.tools.trim.undo')}
            disabled={currentActionIndex === 0 || currentActionIndex === -1}
            icon="undo"
            onClick={undoLastAction}
            hoverText={t('my_video.tools.trim.undo.no_action_tooltip')}
            hoverOnDisabled
            classNameButton={styles.actionBtn}
            classNameTooltip={styles.tooltip}
          />
          <SidebarButton
            label={t('my_video.tools.trim.redo')}
            disabled={trimmingActionsBuffer.length - 1 === 0 || currentActionIndex >= trimmingActionsBuffer.length - 1}
            icon="redo"
            onClick={redoLastAction}
            hoverText={t('my_video.tools.trim.redo.no_action_tooltip')}
            hoverOnDisabled
            classNameButton={styles.actionBtn}
            classNameTooltip={styles.tooltip}
          />
          {enableSplit && (
            <SidebarButton
              disabled={
                switchingTrimming ||
                controlInUse ||
                Math.abs(currentProgressSeconds - startTrimStamp) <= MIN_SPLIT_DURATION / (activeScene?.playbackRate || 1) ||
                Math.abs(currentProgressSeconds - endTrimStamp) <= MIN_SPLIT_DURATION / (activeScene?.playbackRate || 1)
              }
              label={t('my_video.tools.trim.split')}
              icon="split"
              onClick={handleSplitVideo}
              hoverText={t('my_video.tools.trim.split_disabled_message')}
              hoverOnDisabled
              classNameButton={styles.splitBtn}
            />
          )}
        </div>
      </div>
      <div className={styles.footer}>
        <CustomButton
          variant="primary"
          label={saveButtonLabel.length ? saveButtonLabel : t('my_video.tools.trim.publish')}
          onClick={handleTrimming}
          disabled={currentActionIndex === 0 && confirmOnClose}
          className={styles.controlBtn}
        />
        <CustomButton
          variant="secondary"
          onClick={handleRevert}
          disabled={startTrimStamp === 0 && endTrimStamp * (activeScene?.playbackRate || 1) === trimmingVideoDuration}
          label={t('my_video.tools.trim.revert')}
          className={styles.controlBtn}
        />
      </div>
    </div>
  );
};
